import React from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import TextBox from './components/TextBox';
import SelectBox from './components/SelectBox';
import DateInput from './components/DateInput';
import classes from './Logs.module.css';
import Tags from './components/Tags';

const BASE_URL = process.env.REACT_APP_BASE_URL || '';

// S-11: audit-log CSV export. Same filter values the on-screen table
// uses; server-side handler reapplies tenant scoping and streams up to
// 10k rows. Rendered as a small text button next to the tag list so it
// doesn't fight for space with the primary filter controls.
async function exportLogsCsv({ searchKey, dateRange, selectedTags, selectedUserId }) {
  const params = new URLSearchParams();
  if (searchKey) params.set('search', searchKey);
  if (selectedTags && selectedTags.length) params.set('tags', selectedTags.join(',').toLowerCase());
  if (selectedUserId) params.set('user', selectedUserId);
  if (dateRange?.startDate && dateRange?.endDate) {
    const fmt = d => { const [y, m, day] = d.split('-'); return `${m}-${day}-${y}`; };
    params.set('startDate', fmt(dateRange.startDate));
    params.set('endDate',   fmt(dateRange.endDate));
  }
  try {
    const resp = await axios.get(`${BASE_URL}/logs/export?${params}`, { responseType: 'blob', withCredentials: true });
    const blobUrl = window.URL.createObjectURL(new Blob([resp.data], { type: 'text/csv;charset=utf-8' }));
    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = `audit-logs-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => window.URL.revokeObjectURL(blobUrl), 0);
  } catch (err) {
    alert(err.response?.data?.message || err.message || 'Export failed');
  }
}

const Filters = ({ searchKey, setSearchKey, dateRange, setDateRange, selectedRange, setSlectedRange, selectedTags, setSelectedTags, setPageNumber, selectedUserId, setSelectedUserId, users }) => {
  const { t } = useTranslation();
  // const [selectedRange, setSlectedRange] = React.useState('all');

  React.useEffect(() => {
    const today = new Date(Date.now());
    let fromDate = new Date();
    fromDate.setHours(0, 0, 0, 0);

    let toDate = new Date();
    toDate.setHours(0, 0, 0, 0);

    switch (selectedRange) {
      case 'all':
        setDateRange({ startDate: '', endDate: '' });
        break;
      case '30Days':
        fromDate.setDate(today.getDate() - 29);
        toDate.setDate(today.getDate() + 1);
        setDateRange({ startDate: fromDate.toISOString().split('T')[0], endDate: toDate.toISOString().split('T')[0] });
        setPageNumber(1);
        break;
      case '7Days':
        fromDate.setDate(today.getDate() - 6);
        toDate.setDate(today.getDate() + 1);
        setDateRange({ startDate: fromDate.toISOString().split('T')[0], endDate: toDate.toISOString().split('T')[0] });
        setPageNumber(1);
        break;
      case 'custom':
        setDateRange({ startDate: '', endDate: '' });
        setPageNumber(1);
        break;
      default:
        break;
    }
  }, [selectedRange]);

  return (
    <div className={classes.filter_container}>
      <div className='flex' style={{ gap: '2rem', flexWrap: 'wrap' }}>
        <TextBox
          label={t('screens.logs.searchLogs')}
          placeholder={t('screens.logs.searchPlaceholder')}
          icon='search'
          value={searchKey}
          onChange={e => {
            setSearchKey(e.target.value);
            setPageNumber(1);
          }}
        />

        <SelectBox label={t('screens.logs.dateRange')} value={selectedRange} onChange={e => setSlectedRange(e.target.value)}>
          <option value='all'>{t('screens.logs.allLogs')}</option>
          <option value='30Days'>{t('screens.logs.last30Days')}</option>
          <option value='7Days'>{t('screens.logs.last7Days')}</option>
          <option value='custom'>{t('screens.logs.customRange')}</option>
        </SelectBox>

        <SelectBox label={t('screens.logs.filterByUser')} value={selectedUserId} onChange={e => { setSelectedUserId(e.target.value); setPageNumber(1); }}>
          <option value=''>{t('screens.logs.allUsers')}</option>
          {users && users.length > 0 ? (
            users.map(user => (
              <option key={user._id} value={user._id}>
                {user.name} ({user.email})
              </option>
            ))
          ) : null}
        </SelectBox>

        {selectedRange === 'custom' && (
          <>
            <DateInput
              label={t('screens.logs.startDate')}
              value={dateRange.startDate}
              max={new Date().toISOString().split('T')[0]}
              onChange={e => setDateRange(prevval => ({ ...prevval, startDate: e.target.value }))}
            />
            <DateInput
              label={t('screens.logs.endDate')}
              value={dateRange.endDate}
              max={new Date().toISOString().split('T')[0]}
              onChange={e => setDateRange(prevval => ({ ...prevval, endDate: e.target.value }))}
            />
          </>
        )}
      </div>

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
        <Tags selectedTags={selectedTags} setSelectedTags={setSelectedTags} setPageNumber={setPageNumber} />
        <button
          type='button'
          onClick={() => exportLogsCsv({ searchKey, dateRange, selectedTags, selectedUserId })}
          style={{
            padding: '7px 14px',
            background: '#fff',
            color: '#1e293b',
            border: '1px solid #e2e8f0',
            borderRadius: 8,
            fontSize: 12,
            fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          {t('screens.logs.exportCsv', { defaultValue: 'Export CSV' })}
        </button>
      </div>
    </div>
  );
};

export default Filters;
